// ─── Stack Trace Parser ─────────────────────────────────────────────────────

/**
 * Clean Vite dev server URLs to relative project paths.
 * "http://localhost:5173/src/composables/useWorkflowData.js?t=1234"
 *  → "src/composables/useWorkflowData.js"
 */
function cleanFilePath(rawPath) {
  let p = rawPath
  p = p.replace(/^https?:\/\/[^/]+\//, '')
  p = p.replace(/\?.*$/, '')
  p = p.replace(/^\//, '')
  return p
}

/**
 * Parse a JavaScript stack trace string into structured frames.
 * Handles Chrome/V8 and Firefox formats.
 */
export function parseStackTrace(stack) {
  if (!stack) return []
  const frames = []
  const lines = stack.split('\n')

  for (const rawLine of lines) {
    const line = rawLine.trim()

    // Chrome/V8: "at functionName (file:line:column)" or "at file:line:column"
    const chromeMatch = line.match(/^at\s+(?:(.+?)\s+\()?(.+?):(\d+):(\d+)\)?$/)
    if (chromeMatch) {
      frames.push({
        function: chromeMatch[1] || '<anonymous>',
        file: cleanFilePath(chromeMatch[2]),
        line: parseInt(chromeMatch[3], 10),
        column: parseInt(chromeMatch[4], 10),
      })
      continue
    }

    // Firefox: "functionName@file:line:column"
    const firefoxMatch = line.match(/^(.+?)@(.+?):(\d+):(\d+)$/)
    if (firefoxMatch) {
      frames.push({
        function: firefoxMatch[1] || '<anonymous>',
        file: cleanFilePath(firefoxMatch[2]),
        line: parseInt(firefoxMatch[3], 10),
        column: parseInt(firefoxMatch[4], 10),
      })
    }
  }

  return frames
}

/**
 * Find the first stack frame pointing to project source code.
 * Skips node_modules, Vite internals, browser extensions.
 */
export function findUserCodeFrame(frames) {
  return frames.find(f =>
    f.file &&
    !f.file.includes('node_modules') &&
    !f.file.startsWith('chrome-extension') &&
    !f.file.includes('__vite') &&
    !f.file.includes('@vite')
  ) || frames[0] || null
}

// ─── Error Diagnosis Engine ─────────────────────────────────────────────────

const DIAGNOSIS_RULES = [
  {
    test: (type, msg) => type === 'TypeError' && /cannot read prop.+of (null|undefined)/i.test(msg),
    category: 'null-reference',
    cause: (msg) => {
      const prop = (msg.match(/property '(\w+)'/i) || msg.match(/property "(\w+)"/i) || [])[1] || 'unknown'
      return `Mencoba mengakses property '${prop}' dari nilai null/undefined`
    },
    fix: (msg) => {
      const prop = (msg.match(/property '(\w+)'/i) || msg.match(/property "(\w+)"/i) || [])[1] || 'property'
      return `Tambahkan optional chaining (?.) atau null check sebelum mengakses .${prop}. Contoh: obj?.${prop} atau if (obj) { obj.${prop} }`
    },
    confidence: 'high',
  },
  {
    test: (type, msg) => type === 'TypeError' && /is not a function/i.test(msg),
    category: 'type-mismatch',
    cause: (msg) => {
      const fn = (msg.match(/(.+?) is not a function/i) || [])[1] || 'target'
      return `Nilai '${fn}' bukan function — kemungkinan undefined atau tipe data salah`
    },
    fix: () => `Periksa apakah variable/method sudah didefinisikan dan di-import dengan benar. Cek typo pada nama function.`,
    confidence: 'high',
  },
  {
    test: (type, msg) => type === 'TypeError' && /assignment to (constant|const)/i.test(msg),
    category: 'const-assignment',
    cause: () => `Mencoba mengubah nilai variable yang dideklarasikan dengan const`,
    fix: () => `Ganti deklarasi dari 'const' ke 'let' jika variable memang perlu diubah, atau gunakan spread operator / Object.assign untuk pendekatan immutable.`,
    confidence: 'high',
  },
  {
    test: (type) => type === 'TypeError',
    category: 'type-error',
    cause: (msg) => `TypeError: ${msg}`,
    fix: () => `Periksa tipe data pada variable yang terlibat. Pastikan nilai tidak null/undefined sebelum memanggil method.`,
    confidence: 'medium',
  },
  {
    test: (type, msg) => type === 'ReferenceError' && /is not defined/i.test(msg),
    category: 'undefined-variable',
    cause: (msg) => {
      const varName = (msg.match(/(\w+) is not defined/i) || [])[1] || 'unknown'
      return `Variable '${varName}' belum dideklarasikan atau belum di-import`
    },
    fix: (msg) => {
      const varName = (msg.match(/(\w+) is not defined/i) || [])[1] || 'variabel'
      return `Pastikan '${varName}' sudah di-import atau dideklarasikan. Cek: (1) typo pada nama, (2) missing import statement, (3) scope yang salah.`
    },
    confidence: 'high',
  },
  {
    test: (type) => type === 'SyntaxError',
    category: 'syntax',
    cause: (msg) => `Syntax error: ${msg}`,
    fix: () => `Periksa syntax di sekitar baris error. Kemungkinan: bracket/parenthesis tidak matching, koma berlebih, atau keyword yang salah.`,
    confidence: 'medium',
  },
  {
    test: (type, msg) => type === 'RangeError' && /maximum call stack/i.test(msg),
    category: 'infinite-recursion',
    cause: () => `Infinite recursion — fungsi memanggil dirinya sendiri tanpa batas`,
    fix: () => `Tambahkan base case / stopping condition pada recursive function. Periksa apakah ada circular dependency antara computed properties atau watchers.`,
    confidence: 'high',
  },
  {
    test: (_type, msg) => /fetch|network|ERR_CONNECTION|ECONNREFUSED|Failed to fetch/i.test(msg),
    category: 'network',
    cause: () => `Gagal melakukan network request — server mungkin tidak aktif atau URL salah`,
    fix: () => `Periksa: (1) apakah server/API sudah running, (2) URL endpoint sudah benar, (3) CORS headers sudah dikonfigurasi, (4) network connectivity.`,
    confidence: 'medium',
  },
  {
    test: (type, msg) => /JSON/.test(type) || /JSON\.parse|unexpected token|unexpected end of/i.test(msg),
    category: 'json-parse',
    cause: () => `Response yang diterima bukan format JSON valid — kemungkinan server mengirim HTML error page atau empty response`,
    fix: () => `Periksa: (1) response status code sebelum parsing, (2) Content-Type header dari server, (3) apakah endpoint mengembalikan JSON yang valid.`,
    confidence: 'medium',
  },
  {
    test: (_type, msg) => /component.*not found|unknown.*element|failed to resolve component/i.test(msg),
    category: 'vue-component',
    cause: () => `Vue component tidak ditemukan — belum di-register atau salah nama`,
    fix: () => `Pastikan component sudah di-import dan di-register. Periksa casing (PascalCase vs kebab-case).`,
    confidence: 'high',
  },
  {
    test: (_type, msg) => /CORS|cross-origin|Access-Control/i.test(msg),
    category: 'cors',
    cause: () => `Request diblokir oleh CORS policy — server tidak mengizinkan origin ini`,
    fix: () => `Konfigurasi CORS headers di server: Access-Control-Allow-Origin, Access-Control-Allow-Methods, Access-Control-Allow-Headers.`,
    confidence: 'high',
  },
  {
    // Database/SQL errors
    test: (_type, msg) => /ESOCKET|ELOGIN|ConnectionError|RequestError|sql/i.test(msg),
    category: 'database',
    cause: (msg) => `Database error: ${msg.substring(0, 120)}`,
    fix: () => `Periksa: (1) koneksi database (host, port, credentials), (2) query SQL syntax, (3) apakah table/column ada, (4) pool connection limit.`,
    confidence: 'medium',
  },
  {
    // Catch-all
    test: () => true,
    category: 'unknown',
    cause: (msg) => `Error: ${msg}`,
    fix: () => `Periksa stack trace untuk menemukan lokasi error. Tambahkan try/catch dan logging untuk debugging lebih lanjut.`,
    confidence: 'low',
  },
]

/**
 * Auto-diagnose an error based on its type and message.
 */
export function diagnoseError(errorType, message) {
  const rule = DIAGNOSIS_RULES.find(r => r.test(errorType, message))
  if (!rule) return null
  return {
    estimatedCause: rule.cause(message),
    suggestedFix: rule.fix(message),
    category: rule.category,
    confidence: rule.confidence,
  }
}

// ─── Indonesian Date Formatter ──────────────────────────────────────────────

const HARI = ['Minggu', 'Senin', 'Selasa', 'Rabu', 'Kamis', 'Jumat', 'Sabtu']
const BULAN = [
  'Januari', 'Februari', 'Maret', 'April', 'Mei', 'Juni',
  'Juli', 'Agustus', 'September', 'Oktober', 'November', 'Desember',
]

export function formatIndonesianDateTime(isoString) {
  const d = new Date(isoString)
  const hari = HARI[d.getDay()]
  const tgl = d.getDate()
  const bulan = BULAN[d.getMonth()]
  const tahun = d.getFullYear()
  const jam = String(d.getHours()).padStart(2, '0')
  const menit = String(d.getMinutes()).padStart(2, '0')
  const detik = String(d.getSeconds()).padStart(2, '0')
  return `${hari}, ${tgl} ${bulan} ${tahun} — ${jam}:${menit}:${detik} WIB`
}

// ─── AI Report Generator ────────────────────────────────────────────────────

/**
 * Generate a structured AI-ready error report in markdown format.
 * Designed to be copy-pasted into AI for automatic fix + TDD generation.
 */
export function generateAIReport(entry) {
  const lines = []

  lines.push('# ERROR REPORT — AI Analysis Request')
  lines.push('')
  lines.push('## Context')
  lines.push(`- **Project**: uprint-reporting-dashboard (Vue 3 + Vite)`)
  lines.push(`- **Date/Time**: ${entry.timestampFormatted || entry.timestamp}`)
  lines.push(`- **Error ID**: ${entry.id}`)
  lines.push(`- **Severity**: ${entry.severity}`)
  lines.push(`- **Source**: ${entry.source}`)
  lines.push('')

  lines.push('## Error Location')
  lines.push(`- **File**: \`${entry.file || 'unknown'}\``)
  lines.push(`- **Module**: \`${entry.module || 'unknown'}\``)
  lines.push(`- **Component**: \`${entry.component || 'N/A'}\``)
  lines.push(`- **Function**: \`${entry.function || 'N/A'}\``)
  if (entry.line) {
    lines.push(`- **Line**: ${entry.line}${entry.column ? `, Column: ${entry.column}` : ''}`)
  }
  lines.push('')

  lines.push('## Error Details')
  lines.push(`- **Type**: \`${entry.type}\``)
  lines.push(`- **Message**: ${entry.message}`)
  lines.push('')

  if (entry.codeSnippet && entry.codeSnippet.lines && entry.codeSnippet.lines.length > 0) {
    lines.push('## Code Snippet')
    lines.push(`File: \`${entry.codeSnippet.file}\` (lines ${entry.codeSnippet.startLine}-${entry.codeSnippet.endLine})`)
    lines.push('```javascript')
    for (const l of entry.codeSnippet.lines) {
      const marker = l.isErrorLine ? '  // <-- ERROR HERE' : ''
      lines.push(`${String(l.lineNumber).padStart(4, ' ')} | ${l.code}${marker}`)
    }
    lines.push('```')
    lines.push('')
  }

  if (entry.stackFrames && entry.stackFrames.length > 0) {
    lines.push('## Stack Trace (parsed)')
    for (const frame of entry.stackFrames.slice(0, 8)) {
      lines.push(`- \`${frame.function}\` at \`${frame.file}:${frame.line}:${frame.column}\``)
    }
    lines.push('')
  }

  if (entry.stack) {
    lines.push('## Raw Stack Trace')
    lines.push('```')
    lines.push(entry.stack.split('\n').slice(0, 10).join('\n'))
    lines.push('```')
    lines.push('')
  }

  if (entry.diagnosis) {
    lines.push('## Auto-Diagnosis')
    lines.push(`- **Category**: ${entry.diagnosis.category}`)
    lines.push(`- **Estimated Cause**: ${entry.diagnosis.estimatedCause}`)
    lines.push(`- **Suggested Fix**: ${entry.diagnosis.suggestedFix}`)
    lines.push(`- **Confidence**: ${entry.diagnosis.confidence}`)
    lines.push('')
  }

  if (entry.context && Object.keys(entry.context).length > 0) {
    lines.push('## Additional Context')
    lines.push('```json')
    lines.push(JSON.stringify(entry.context, null, 2))
    lines.push('```')
    lines.push('')
  }

  lines.push('## Instructions for AI')
  lines.push('1. Analyze the error above and confirm or refine the auto-diagnosis.')
  lines.push('2. Provide the EXACT code fix with before/after comparison.')
  lines.push('3. Generate a TDD test suite (using Vitest) that:')
  lines.push('   a. Reproduces this error (red test)')
  lines.push('   b. Validates the fix (green test)')
  lines.push('   c. Covers edge cases related to this error type')
  lines.push('4. If the fix requires changes in multiple files, list all files.')
  lines.push('')

  return lines.join('\n')
}

// ─── Pattern Report Generator (<#Error#> format) ────────────────────────────

const APP_NAME = 'uprint-reporting-dashboard'

/**
 * Generate error report in <#Error#> pattern format (client-side fallback).
 * @param {object} entry - The error entry
 * @param {object|null} trackerInfo - DB tracker info { errorCode, noDetect, firstShow, lastShow, isSolved }
 */
export function generatePatternReport(entry, trackerInfo = null) {
  const errorCode = trackerInfo?.errorCode || entry.errorCode || `${entry.type}_local`
  const noDetect = trackerInfo?.noDetect ?? 1
  const firstShow = trackerInfo?.firstShow || entry.timestamp
  const lastShow = trackerInfo?.lastShow || entry.timestamp
  const isSolved = trackerInfo?.isSolved ? 1 : 0

  const errorSource = [
    entry.file ? entry.file + (entry.line ? `:${entry.line}` : '') : '',
    entry.module || '',
    entry.function || '',
  ].filter(Boolean).join(' | ') || entry.source || 'unknown'

  const diagnoseText = entry.diagnosis
    ? `Category: ${entry.diagnosis.category} (Confidence: ${entry.diagnosis.confidence})\nPenyebab: ${entry.diagnosis.estimatedCause}\nSaran Perbaikan: ${entry.diagnosis.suggestedFix}`
    : 'Diagnosis tidak tersedia'

  // Build body
  const bodyParts = []
  bodyParts.push(`Error Type: ${entry.type}`)
  bodyParts.push(`Message: ${entry.message}`)
  bodyParts.push('')
  if (entry.file) {
    bodyParts.push(`File: ${entry.file}`)
    if (entry.line) bodyParts.push(`Line: ${entry.line}${entry.column ? `, Column: ${entry.column}` : ''}`)
  }
  if (entry.component) bodyParts.push(`Component: ${entry.component}`)
  if (entry.function) bodyParts.push(`Function: ${entry.function}`)
  bodyParts.push('')

  if (entry.codeSnippet && entry.codeSnippet.lines && entry.codeSnippet.lines.length > 0) {
    bodyParts.push(`Code Snippet (${entry.codeSnippet.file} lines ${entry.codeSnippet.startLine}-${entry.codeSnippet.endLine}):`)
    for (const l of entry.codeSnippet.lines) {
      const marker = l.isErrorLine ? '  // <-- ERROR HERE' : ''
      bodyParts.push(`${String(l.lineNumber).padStart(4, ' ')} | ${l.code}${marker}`)
    }
    bodyParts.push('')
  }

  if (entry.stackFrames && entry.stackFrames.length > 0) {
    bodyParts.push('Stack Trace:')
    for (const frame of entry.stackFrames.slice(0, 8)) {
      bodyParts.push(`  ${frame.function} at ${frame.file}:${frame.line}:${frame.column}`)
    }
    bodyParts.push('')
  }

  if (entry.context && Object.keys(entry.context).length > 0) {
    bodyParts.push('Context:')
    for (const [key, val] of Object.entries(entry.context)) {
      if (val !== undefined && val !== null && val !== '') {
        bodyParts.push(`  ${key}: ${typeof val === 'object' ? JSON.stringify(val) : val}`)
      }
    }
  }

  const body = bodyParts.join('\n')

  const lines = []
  lines.push('<#Error#>')
  lines.push('')
  lines.push(`<#Application#>${APP_NAME}<#/Application#>`)
  lines.push('')
  lines.push(`<#ErrorCode#>${errorCode}<#/ErrorCode#>`)
  lines.push('')
  lines.push(`<#ErrorDate#>${entry.timestampFormatted || entry.timestamp}<#/ErrorDate#>`)
  lines.push('')
  lines.push(`<#ErrorTitle#>${entry.type}: ${entry.message}<#/ErrorTitle#>`)
  lines.push('')
  lines.push(`<#ErrorBody#>`)
  lines.push(body)
  lines.push(`<#/ErrorBody#>`)
  lines.push('')
  lines.push(`<#ErrorSource#>${errorSource}<#/ErrorSource#>`)
  lines.push('')
  lines.push(`<#ErrorDiagnose#>`)
  lines.push(diagnoseText)
  lines.push(`<#/ErrorDiagnose#>`)
  lines.push('')
  lines.push(`<#ErrorNoDetect#>${noDetect}<#/ErrorNoDetect#>`)
  lines.push('')
  lines.push(`<#ErrorFirstShow#>${firstShow}<#/ErrorFirstShow#>`)
  lines.push('')
  lines.push(`<#ErrorLastShow#>${lastShow}<#/ErrorLastShow#>`)
  lines.push('')
  lines.push(`<#IsErrorSolved#>${isSolved}<#/IsErrorSolved#>`)
  lines.push('')
  lines.push('<#/Error#>')

  return lines.join('\n')
}
